import { z } from 'zod'

import type { ChatHistoryMessage } from './chat-history'
import { deleteGroupOrdersInputSchema, deleteGroupOrdersResponseSchema } from './delete-orders'
import { pickOrderItemChatHistory } from './order-item-chat'
import { apiBlobRequest, apiRequest } from '@/lib/api/client'
import { isNotFound } from '@/lib/api/errors'

import { FALLBACK_INDONESIA_PHONE_CODE_ID } from '@/features/phone-code/defaults'

import { toGroupAddress, type GroupAddress } from './address'
import {
  groupDetailPayloadSchema,
  groupListPayloadSchema,
  groupOrderCountPayloadSchema,
  processGroupOrdersResponseSchema,
  productNameSuggestionsPayloadSchema,
} from './schema'
import { saveGroupAddressResponseSchema } from './sender-schema'
import {
  chargeGroupOrderResponseSchema,
  createGroupOrderResponseSchema,
  type ChargeGroupOrderInput,
  type CreateGroupOrderInput,
  type EditGroupOrderInput,
} from './order-charge'

interface FetchGroupsParams {
  page?: number
  perPage?: number
  signal?: AbortSignal
}

export function fetchGroups({ page = 1, perPage = 100, signal }: FetchGroupsParams = {}) {
  return apiRequest('/v1/algo-pos-be/liotitip/group', {
    schema: groupListPayloadSchema,
    searchParams: { page, per_page: perPage },
    signal,
  })
}

interface FetchGroupOrdersParams {
  groupId: string
  /** Comma-joined list, e.g. `COMPLETED,CANCELLED`. */
  orderStatus?: string
  invoiceStatus?: string
  /** Free-text match on the order's products, applied server-side. */
  search?: string
  page?: number
  perPage?: number
  signal?: AbortSignal
}

function orderPath(groupId: string) {
  return `/v1/algo-pos-be/liotitip/group/${encodeURIComponent(groupId)}/order`
}

/** Returns the group plus the page of orders matching the filters. */
export function fetchGroupDetail({
  groupId,
  orderStatus,
  invoiceStatus,
  search,
  page = 1,
  perPage = 100,
  signal,
}: FetchGroupOrdersParams) {
  return apiRequest(orderPath(groupId), {
    schema: groupDetailPayloadSchema,
    searchParams: {
      order_status: orderStatus,
      invoice_status: invoiceStatus,
      search,
      page,
      per_page: perPage,
    },
    signal,
  })
}

/**
 * Just the match count for a filter. Asks for a single row because only
 * `pagination.total_count` is read — the group's own counters are stale on some
 * groups, so the badges cannot be sourced from them.
 */
export function fetchGroupOrderCount({
  groupId,
  orderStatus,
  invoiceStatus,
  search,
  signal,
}: FetchGroupOrdersParams) {
  return apiRequest(orderPath(groupId), {
    schema: groupOrderCountPayloadSchema,
    searchParams: {
      order_status: orderStatus,
      invoice_status: invoiceStatus,
      search,
      page: 1,
      per_page: 1,
    },
    signal,
  })
}

export async function processGroupOrders(
  groupId: string,
  orderExternalIds: string[],
  signal?: AbortSignal,
) {
  return apiRequest(`${orderPath(groupId)}/process`, {
    method: 'POST',
    schema: processGroupOrdersResponseSchema,
    body: { order_external_ids: orderExternalIds },
    signal,
  })
}

export async function downloadGroupOrderLabels(
  groupId: string,
  orderExternalIds: string[],
  signal?: AbortSignal,
  printType: LabelPrintType = 'standard_label',
) {
  return apiBlobRequest(`${orderPath(groupId)}/print-resi`, {
    searchParams: { order_external_ids: orderExternalIds.join(','), print_type: printType },
    signal,
  })
}

export async function fetchGroupAddress(
  groupId: string,
  signal?: AbortSignal,
): Promise<GroupAddress | null> {
  try {
    const raw = await apiRequest(
      `/v1/algo-pos-be/liotitip/group/${encodeURIComponent(groupId)}/address`,
      { schema: z.unknown(), signal },
    )
    return toGroupAddress(raw)
  } catch (error) {
    if (isNotFound(error)) return null
    throw error
  }
}

export interface SaveGroupAddressInput {
  groupId: string
  name: string
  routeId: number
  phoneNumber: string
  address: string
  additionalAddress?: string
  latitude: number
  longitude: number
  phoneCodeId?: number
  socialMediaUrls?: string[]
  logo?: File | null
  isRemoveLogo?: boolean
}

/** algo-pos-be expects MSISDN with country code `62` and no leading `+`. */
function toGroupAddressPhoneNumber(phone: string) {
  const digits = phone.replace(/\D/g, '').replace(/^0+/, '')
  return digits.startsWith('62') ? digits : `62${digits}`
}

export async function saveGroupAddress(input: SaveGroupAddressInput, signal?: AbortSignal) {
  const form = new FormData()
  form.append('name', input.name)
  form.append('route_id', String(input.routeId))
  form.append('phone_number', toGroupAddressPhoneNumber(input.phoneNumber))
  form.append('address', input.address)
  form.append('additional_address', input.additionalAddress ?? '')
  form.append('latitude', String(input.latitude))
  form.append('longitude', String(input.longitude))
  form.append('phone_code_id', String(input.phoneCodeId ?? FALLBACK_INDONESIA_PHONE_CODE_ID))
  if (input.isRemoveLogo) form.append('is_remove_logo', 'true')
  const urls = (input.socialMediaUrls ?? []).map((url) => url.trim()).filter(Boolean)
  if (urls.length > 0) form.append('social_media_urls', urls.join(','))
  if (input.logo) form.append('logo', input.logo)

  return apiRequest(`/v1/algo-pos-be/liotitip/group/${encodeURIComponent(input.groupId)}/address`, {
    method: 'POST',
    schema: saveGroupAddressResponseSchema,
    body: form,
    signal,
  })
}

/** Cancels a pre-shipment LioTitip order (tab Ditagihkan). IN_PROCESS uses `cancelShipments`. */
export function cancelGroupOrder(groupId: string, orderId: string, signal?: AbortSignal) {
  return apiRequest(
    `/v1/algo-pos-be/liotitip/group/${encodeURIComponent(groupId)}/order/${encodeURIComponent(orderId)}/cancel`,
    {
      method: 'POST',
      schema: z.object({}).passthrough(),
      signal,
    },
  )
}

/** Removes an unpaid order entirely (Belum bayar tab). */
export function deleteGroupOrder(groupId: string, orderId: string, signal?: AbortSignal) {
  return apiRequest(orderItemPath(groupId, orderId), {
    method: 'DELETE',
    schema: z.object({ message: z.string() }),
    signal,
  })
}

function orderItemPath(groupId: string, orderId: string) {
  return `/v1/algo-pos-be/liotitip/group/${encodeURIComponent(groupId)}/order/${encodeURIComponent(orderId)}`
}

/** Deletes explicit unpaid orders, or every unpaid order with the sole ID `ALL`. */
export function deleteGroupOrders(groupId: string, orderIds: string[], signal?: AbortSignal) {
  const id = z.string().trim().min(1).parse(groupId)
  const body = deleteGroupOrdersInputSchema.parse({ order_external_ids: orderIds })
  return apiRequest(orderPath(id), {
    method: 'DELETE',
    schema: deleteGroupOrdersResponseSchema,
    body,
    signal,
  })
}

export function createGroupOrder(
  groupId: string,
  body: CreateGroupOrderInput,
  signal?: AbortSignal,
) {
  return apiRequest(orderPath(groupId), {
    method: 'POST',
    schema: createGroupOrderResponseSchema,
    body,
    signal,
  })
}

export function editGroupOrder(
  groupId: string,
  orderId: string,
  body: EditGroupOrderInput,
  signal?: AbortSignal,
) {
  return apiRequest(orderItemPath(groupId, orderId), {
    method: 'PATCH',
    schema: z.object({}).passthrough(),
    body,
    signal,
  })
}

function orderLineItemsPath(groupId: string, orderId: string) {
  return `${orderItemPath(groupId, orderId)}/item`
}

function orderItemsFormBody(orderItems: unknown) {
  const form = new FormData()
  form.append('order_items', JSON.stringify(orderItems))
  return form
}

const emptyItemMutationSchema = z.unknown()

/** PATCH /order/{id}/item — same multipart `order_items` JSON as android-consumer. */
export function editGroupOrderItems(
  groupId: string,
  orderId: string,
  orderItems: unknown,
  signal?: AbortSignal,
) {
  return apiRequest(orderLineItemsPath(groupId, orderId), {
    method: 'PATCH',
    schema: emptyItemMutationSchema,
    body: orderItemsFormBody(orderItems),
    signal,
  })
}

/** POST /order/{id}/item — add new line items (android-consumer addOrderItem). */
export function addGroupOrderItem(
  groupId: string,
  orderId: string,
  orderItems: unknown,
  signal?: AbortSignal,
) {
  return apiRequest(orderLineItemsPath(groupId, orderId), {
    method: 'POST',
    schema: emptyItemMutationSchema,
    body: orderItemsFormBody(orderItems),
    signal,
  })
}

/** DELETE /order/{id}/item/{index} — remove one existing line item. */
export function deleteGroupOrderItem(
  groupId: string,
  orderId: string,
  itemIndex: number,
  signal?: AbortSignal,
) {
  return apiRequest(`${orderLineItemsPath(groupId, orderId)}/${itemIndex}`, {
    method: 'DELETE',
    schema: emptyItemMutationSchema,
    signal,
  })
}

export function chargeGroupOrder(
  groupId: string,
  orderId: string,
  body: ChargeGroupOrderInput,
  signal?: AbortSignal,
) {
  return apiRequest(`${orderItemPath(groupId, orderId)}/charge`, {
    method: 'POST',
    schema: chargeGroupOrderResponseSchema,
    body,
    signal,
  })
}

/** Chat lives on `order_items[].chat_history` in the group order list (UNPAID/BILLED only). */
export async function fetchOrderItemChatHistory(
  groupId: string,
  orderId: string,
  itemIndex: number,
  itemArrayIndex?: number,
  signal?: AbortSignal,
): Promise<ChatHistoryMessage[]> {
  for (const orderStatus of ['UNPAID', 'BILLED', 'UNPAID,BILLED'] as const) {
    const detail = await fetchGroupDetail({
      groupId,
      orderStatus,
      page: 1,
      perPage: 100,
      signal,
    })
    const order = detail.orders.find((entry) => entry.id === orderId)
    const messages = pickOrderItemChatHistory(order, itemIndex, itemArrayIndex)
    if (messages.length > 0) return messages
    if (order) return messages
  }
  return []
}

export function fetchProductNameSuggestions({
  groupId,
  query,
  limit = 10,
  signal,
}: {
  groupId: string
  query: string
  limit?: number
  signal?: AbortSignal
}) {
  return apiRequest(
    `/v1/algo-pos-be/liotitip/group/${encodeURIComponent(groupId)}/product-name-suggestions`,
    {
      schema: productNameSuggestionsPayloadSchema,
      searchParams: { q: query, limit },
      signal,
    },
  ).then((payload) => payload.suggestions)
}
import type { LabelPrintType } from './label-type'
